import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Location } from "../../core/models/location.model";
import { Observable, of } from "rxjs";
import { map, switchMap, catchError } from "rxjs/operators";
import { LogService } from "../../core/services/log.service";
import { EnvironmentConfig } from "../../core/models/environment-config.model";
import { Address } from "../../core/models/address.model";
import { IpGeoLocationService } from "../../core/core";

declare var environment: EnvironmentConfig;
@Injectable({
  providedIn: "root",
})
export class AzureMapService {
  private url = "https://atlas.microsoft.com";
  constructor(private httpClient: HttpClient, private logger: LogService, private ipGeoLocationService: IpGeoLocationService) {}

  geoCodeNinja(location: string): Observable<Location> {
    const split = location.split("-");
    const stateAbbr = split[split.length - 1].trim();
    const city = split.slice(0, -1).join(" ").trim();
    const state = this.ipGeoLocationService.getStateName(stateAbbr);
    const country = "USA";
    const url = "https://api.api-ninjas.com/v1/geocoding";
    const headers = {
      "X-Api-Key": "y9Gc4wG/dVcHvJzMQTNj/g==YUn9qkKqfayqctNc",
    };
    const params = new HttpParams().append("city", city.trim()).append("country", state);
    // 1. Try API Ninja first
    return this.httpClient.get(url, { params, headers }).pipe(
      switchMap((response: any) => {
        if (response && response.length > 0) {
          let bestResult = response.find(d => d.name === city && d.state === state);
          if (bestResult) {
            this.logger.trackEvent("geoCodeNinja-best-result-success");
            const resp = new Location();
            resp.latitude = bestResult.latitude;
            resp.longitude = bestResult.longitude;
            resp.address.city = bestResult.name;
            resp.address.stateProvince = this.ipGeoLocationService.getStateCode(bestResult.state);
            resp.address.country = bestResult.country;
            resp.name = `${resp.address.city}, ${resp.address.stateProvince}`;
            return of(resp);
          }
        }
        // 2. Fallback to JSON file
        const jsonUrl = "https://proddevops.blob.core.windows.net/pubweb/json/location/formatted_us_zips_minified.json";
        return this.httpClient.get<any[]>(jsonUrl).pipe(
          switchMap((cities: any[]) => {
            const found = cities.find(c => c.c.toLowerCase() === city.toLowerCase() && c.s.toLowerCase() === stateAbbr.toLowerCase());
            if (found) {
              const resp = new Location();
              resp.latitude = found.la;
              resp.longitude = found.lo;
              resp.address.city = found.c;
              resp.address.stateProvince = found.s;
              resp.address.country = "USA";
              resp.name = `${found.c}, ${found.s}`;
              return of(resp);
            }
            // 3. Fallback to Mapbox, then Azure Maps if Mapbox fails
            return this.mapboxGeoCode(location).pipe(catchError(() => this.geoCode(location)));
          }),
          catchError(() =>
            // If JSON file fails, fallback to Mapbox, then Azure Maps if Mapbox fails
            this.mapboxGeoCode(location).pipe(catchError(() => this.geoCode(location))),
          ),
        );
      }),
      catchError(() => {
        // If API Ninja fails, fallback to JSON file, then Mapbox, then Azure Maps
        const jsonUrl = "https://proddevops.blob.core.windows.net/pubweb/json/location/formatted_us_zips_minified.json";
        return this.httpClient.get<any[]>(jsonUrl).pipe(
          switchMap((cities: any[]) => {
            const found = cities.find(c => c.c.toLowerCase() === city.toLowerCase() && c.s.toLowerCase() === stateAbbr.toLowerCase());
            if (found) {
              const resp = new Location();
              resp.latitude = found.la;
              resp.longitude = found.lo;
              resp.address.city = found.c;
              resp.address.stateProvince = found.s;
              resp.address.country = "USA";
              resp.name = `${found.c}, ${found.s}`;
              return of(resp);
            }
            // Fallback to Mapbox, then Azure Maps if Mapbox fails
            return this.mapboxGeoCode(location).pipe(catchError(() => this.geoCode(location)));
          }),
          catchError(() =>
            // If JSON file fails, fallback to Mapbox, then Azure Maps if Mapbox fails
            this.mapboxGeoCode(location).pipe(catchError(() => this.geoCode(location))),
          ),
        );
      }),
    );
  }

  mapboxGeoCode(location: string) {
    const params = new HttpParams().append("access_token", environment.mapboxKey).append("types", "place");
    return this.httpClient.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(location)}.json`, { params }).pipe(
      map((response: any) => {
        const resp = new Location();
        if (response.features && response.features.length > 0) {
          const bestResult = response.features[0];
          resp.latitude = bestResult.center[1];
          resp.longitude = bestResult.center[0];
          resp.address.city = bestResult.text;
          resp.address.country = "USA";
          let stateCode = bestResult.context.find(c => c.id.startsWith("region"))?.short_code;
          stateCode = stateCode.split("-")[1];
          resp.address.stateProvince = stateCode;
          resp.name = `${resp.address.city}, ${stateCode}`;
        }
        return resp;
      }),
    );
  }

  geoCode(location: string): Observable<Location> {
    const params = new HttpParams().append("subscription-key", environment.azureMapKey).append("api-version", "1.0").append("query", location);
    return this.httpClient.get(`${this.url}/search/address/json`, { params }).pipe(
      map((response: any) => {
        const resp = new Location();

        if (response.summary.numResults > 0) {
          const bestResult = response.results[0];
          resp.latitude = bestResult.position.lat;
          resp.longitude = bestResult.position.lon;
          if (bestResult.address.streetNumber && bestResult.address.streetName) {
            resp.address.address1 = `${bestResult.address.streetNumber} ${bestResult.address.streetName}`;
          }
          resp.address.address2 = "";
          resp.address.city = bestResult.address?.postalName || bestResult.address?.municipality || "";
          resp.address.stateProvince = bestResult.address.countrySubdivision;
          resp.address.postalCode = bestResult.address.postalCode;
          resp.address.country = bestResult.address.countryCodeISO3;
          resp.name = `${resp.address.city}, ${resp.address.stateProvince}`;
        }

        return resp;
      }),
    );
  }

  public reverseGeocode(location: Location) {
    const params = new HttpParams()
      .append("subscription-key", environment.azureMapKey)
      .append("api-version", "1.0")
      .append("query", `${location.latitude},${location.longitude}`)
      .append("number", "1"); // only return 1 result

    return this.httpClient.get(`${this.url}/search/address/reverse/json`, { params }).pipe(
      map((resp: any) => {
        if (resp.addresses.length) {
          location.address = new Address();
          let address = resp.addresses[0].address;
          if (address.postalCode && address.streetNameAndNumber && address.municipality && address.countrySubdivision) {
            location.address.address1 = address.streetNameAndNumber;
            location.address.postalCode = address.postalCode;
            location.address.city = address.address?.postalName || address.address?.municipality || "";
            location.address.stateProvince = address.countrySubdivision;
          }

          return location;
        }
      }),
    );
  }

  public getReversedLocation(location: Location, allowPartialAddress: boolean = false) {
    return this.reverseGeocode(location).pipe(
      switchMap(x => {
        // if we have a postal code and address 1 set the address. otherwise set the address to null since we do not have one
        if (x.address.postalCode && x.address.address1) {
          location.address = x.address;
        } else if (x.address.stateProvince && x.address.city && allowPartialAddress) {
          location.address = x.address;
        } else {
          location.address = null;
        }
        return of(location);
      }),
    );
  }
}
